import { defineStore } from 'pinia';
import IPage from '@/interfaces/IPage';
import IImage from '@/interfaces/IImage';
import IDescription from '@/interfaces/IDescription';
import { deduplicateImages } from '@/helpers/images/deduplicateImages';

export const usePagesStore = defineStore('pages', {
  state: () => ({
    pages: new Array<IPage>(),
  }),
  getters: {
    getNumberOfPages(): number {
      return this.pages.length;
    },
    getAllImages(): IImage[] {
      return this.pages.flatMap((page) => page.description.images);
    },
    getNumberOfImages(): number {
      return this.getAllImages.length;
    },
    missingMetaTitleCount(state): number {
      return state.pages.filter(page => !page.metaTitle).length;
    },
    missingMetaDescriptionCount(state): number {
      return state.pages.filter(page => !page.metaDescription).length;
    },
    missingDescriptionCount(state): number {
      return state.pages.filter(page => !page.description.descriptionHtml).length;
    },
    // New getter to count images by type
    getImageTypeCounts(): Record<string, number> {
      return this.getAllImages.reduce((acc: Record<string, number>, image: IImage) => {
        acc[image.type] = (acc[image.type] || 0) + 1;
        return acc;
      }, {});
    },
  },
  actions: {
    addPage(
      pathname: string,
      metaTitle: string,
      metaDescription: string,
      description: IDescription
    ) {
      // Deduplicating images from the description. The primary array is intentionally empty.
      const { dedupedSecondary } = deduplicateImages([], description.images);
      const newPage: IPage = {
        pathname,
        metaTitle,
        metaDescription,
        description: {
          ...description,
          images: dedupedSecondary,
        },
      };
      this.pages.push(newPage);
    },
    clearPageStore() {
      this.pages = new Array<IPage>();
    },
    hasUrl(pathname: string) {
      return this.pages.some((pages) => pages.pathname === pathname);
    },
    getPageByPathname(pathname: string): IPage | undefined {
      return this.pages.find((page) => page.pathname === pathname);
    },
  },
});
